import React, { useEffect } from 'react';
import { ampli } from '@/ampli';
import { AppProviders } from '@/app-providers';
import ErrorBoundary from '@/components/ErrorBoundary';
import ResponsiveHelper from '@/components/response-helper';
import { msalConfig } from '@/config/azure-sso-config';
import { AMPLITUDE_EVX_API_KEY, DEV, GOOGLE_MAPS_API_KEY } from '@/env';
import { queryClient } from '@/query-client';
import { routeTree } from '@/route-tree';
import { Toaster } from '@/shared/components/ui/sonner';
import { authUtils } from '@/shared/lib/utils';
import { store } from '@/store';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import {
  EventType,
  PublicClientApplication,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { Portal } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { APIProvider } from '@vis.gl/react-google-maps';
import { setDefaultOptions } from 'date-fns';
import { es } from 'date-fns/locale';
import momentLocale from 'moment';
import moment from 'moment-timezone';
import UserSessionProvider from 'providers/UserSessionProvider';
import { isIOS, isMobile } from 'react-device-detect';
import { Provider as ReduxProvider } from 'react-redux';

import '@/styles/globals.css';
import '@/styles/layout/_app-layout.scss';
import 'assets/vendors/style';
import 'nprogress/nprogress.css';
import 'moment/dist/locale/es';
import 'moment/locale/es';

import log from '@/lib/logger';

if (!DEV) {
  try {
    const sessionReplayTracking = sessionReplayPlugin({
      forceSessionTracking: true,
      sampleRate: 0.1,
    });
    amplitude.add(sessionReplayTracking);
    ampli.load({
      client: {
        instance: amplitude,
        configuration: {
          autocapture: true,
        },
      },
      disabled: DEV,
    });
    ampli.client.init(AMPLITUDE_EVX_API_KEY, {
      autocapture: true,
    });
  } catch (e) {
    log.error('Amplitude error', e);
  }
}

const msalInstance = new PublicClientApplication(msalConfig);

if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
  }
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: authUtils,
  },
  basepath: '/',
  defaultNotFoundComponent: React.lazy(async () => await import('@/modules/core/pages/not-found')),
});

// * This is for the router to have strong type definitions
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const localeLang = 'es';
momentLocale.locale(localeLang);
moment.locale(localeLang);
moment.defineLocale(
  localeLang,
  // @ts-expect-error moment private property
  (momentLocale.localeData() as unknown as { _config: unknown })._config
);

setDefaultOptions({ locale: es, weekStartsOn: 1 });

const MainApp: React.FC = () => {
  // set default height and overflow for iOS mobile Safari 10+ support.
  useEffect(() => {
    if (isIOS && isMobile) {
      document.body.classList.add('ios-mobile-view-height');
    } else if (document.body.classList.contains('ios-mobile-view-height')) {
      document.body.classList.remove('ios-mobile-view-height');
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <UserSessionProvider>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <AppProviders>
                <MsalProvider instance={msalInstance}>
                  <RouterProvider router={router} />
                </MsalProvider>
              </AppProviders>
              <Portal container={document.body}>
                <Toaster />
              </Portal>
              <ResponsiveHelper />
            </APIProvider>
          </UserSessionProvider>
        </ReduxProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
        <TanStackRouterDevtools router={router} initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default MainApp;
